import { SelectionLevel } from '__generated__/graphql';
import {
  intToSelectionLevelEnum as INT_TO_SELECTION_LEVEL_ENUM,
  selectionLevelEnumToInt as SELECTION_LEVEL_ENUM_TO_INT,
} from './selectionLevelConstants';

/**
 * Shared, dynamic-aware selection-level helpers.
 *
 * The app supports two modes:
 *  - DYNAMIC (feature flag ON): the project carries `selectionLevels`
 *    [{ id, level:Int, name, hidden, order, sentiment }] with arbitrary custom levels (>=5).
 *  - LEGACY  (flag OFF): the fixed 5-level enum + `customSelectionLabels` for renaming/hiding.
 *
 * Everything downstream operates on the integer `selectionLevelValue`. These helpers resolve a
 * project's ordered levels and an application's current level the same way the Kanban board does,
 * so every other consumer (status dropdown, quick actions, filters, …) stays consistent in both
 * modes. They accept the Immutable `currentProject` map straight from redux.
 */

// ─── Enum ↔ Int maps ─────────────────────────────────────────────────────────────
// Values live in the dependency-free `selectionLevelConstants` module (single source of truth,
// importable from places that can't pull in `__generated__`). They are re-exported here with the
// strict SelectionLevel typing the rest of the app relies on; SelectionLevel is a string enum, so
// the casts are pure type assertions with no runtime effect.
export const selectionLevelEnumToInt =
  SELECTION_LEVEL_ENUM_TO_INT as Record<SelectionLevel, number>;
export const intToSelectionLevelEnum =
  INT_TO_SELECTION_LEVEL_ENUM as Record<number, SelectionLevel>;

/** The fixed enum ordering used to reconstruct the legacy view when the flag is off. */
const LEGACY_LEVEL_ORDER: SelectionLevel[] = [
  SelectionLevel.Candidate,
  SelectionLevel.Qualification,
  SelectionLevel.Interview,
  SelectionLevel.NarrowedRound,
  SelectionLevel.Hired,
];

/** i18n key prefix for the default level labels (e.g. applications.search.filter.selection.Candidate). */
export const SELECTION_LEVEL_LABEL_PREFIX = 'applications.search.filter.selection';

export interface SelectionLevelOption {
  /** Integer selection level: 0..4 for defaults, >=5 for custom. This is what mutations/filters send. */
  value: number;
  /**
   * Display label. When `translateLabel` is true this is the enum key (e.g. "Candidate") and the UI
   * must translate it via `${SELECTION_LEVEL_LABEL_PREFIX}.${label}`; otherwise it is a literal name.
   */
  label: string;
  translateLabel: boolean;
  hidden: boolean;
  order: number;
  sentiment?: string | null;
}

/** Build the legacy customSelectionLabels lookup ({ [enumName]: { label, hidden } }) from the project. */
const parseCustomSelectionLabels = (
  currentProject: any
): Record<string, { label?: string; hidden?: boolean }> => {
  const raw = currentProject ? currentProject.get('customSelectionLabels') : null;
  if (!raw) return {};
  const labels: Record<string, { label?: string; hidden?: boolean }> = {};
  const labelsArray = raw.toJS();
  Object.keys(labelsArray).forEach((key) => {
    const item = labelsArray[key];
    labels[item.selectionLevel] = { label: item.name, hidden: item.hidden };
  });
  return labels;
};

/** A dynamic selection level as plain JSON (e.g. from a cross-project GraphQL payload). */
export interface DynamicSelectionLevel {
  level: number;
  name?: string | null;
  hidden?: boolean;
  order?: number;
  sentiment?: string | null;
}

/**
 * Build ordered options from a plain array of dynamic levels. Single source of truth for the
 * dynamic branch — used both for the Immutable per-project data (via `.toJS()`) and for plain
 * cross-project payloads (e.g. the All Applications page) that never live in redux/Immutable.
 */
export const buildSelectionLevelOptionsFromLevels = (
  levels: DynamicSelectionLevel[] | null | undefined
): SelectionLevelOption[] => {
  const options = (levels || []).map((sl) => {
    const value = Number(sl.level);
    const rawName = (sl.name as string) || '';
    const enumKey = intToSelectionLevelEnum[value];
    // An empty name on a default level (e.g. a locked Hired persisted with name="") falls back to
    // the translated default label, so the option is never blank — same as the board.
    const useDefaultLabel = rawName.trim() === '' && !!enumKey;
    return {
      value,
      label: useDefaultLabel ? enumKey : rawName,
      translateLabel: useDefaultLabel,
      hidden: !!sl.hidden,
      order: Number(sl.order ?? value),
      sentiment: sl.sentiment ?? null,
    };
  });
  options.sort((a, b) => a.order - b.order);
  return options;
};

/**
 * Resolve the project's ordered selection levels as integer-valued options.
 * Mirrors the Kanban board's buildColumns: dynamic from `selectionLevels`, otherwise the legacy
 * enum + customSelectionLabels. Sorted by display order ascending (Candidate first, Hired last).
 */
export const getSelectionLevelOptions = (currentProject: any): SelectionLevelOption[] => {
  const rawLevels = currentProject ? currentProject.get('selectionLevels') : null;
  const hasDynamic = rawLevels && rawLevels.size > 0;

  if (hasDynamic) {
    return buildSelectionLevelOptionsFromLevels(rawLevels.toJS());
  }

  // Legacy mode — reconstruct from the fixed enum + custom labels.
  const customLabels = parseCustomSelectionLabels(currentProject);
  return LEGACY_LEVEL_ORDER.map((enumLevel, index) => {
    const cfg = customLabels[enumLevel];
    const customName = cfg?.label;
    return {
      value: selectionLevelEnumToInt[enumLevel],
      label: customName || enumLevel,
      translateLabel: !customName,
      hidden: !!cfg?.hidden,
      order: index,
      sentiment: null,
    };
  });
};

/** Project's levels minus the hidden ones — what level pickers should offer. */
export const getVisibleSelectionLevelOptions = (currentProject: any): SelectionLevelOption[] =>
  getSelectionLevelOptions(currentProject).filter((option) => !option.hidden);

/**
 * The integer level of an application: prefer the dynamic `selectionLevelValue`, fall back to the
 * deprecated `selectionLevel` enum for older data. Accepts an Immutable map or a plain object.
 */
export const getApplicationLevelValue = (application: any): number => {
  if (!application) return 0;
  const read = (key: string) => (application.get ? application.get(key) : application[key]);
  const raw = read('selectionLevelValue');
  if (raw !== null && raw !== undefined) return Number(raw);
  const enumLevel = read('selectionLevel') as SelectionLevel;
  return selectionLevelEnumToInt[enumLevel] ?? 0;
};

/** Resolve the display label for a given level value within a project. */
export const getSelectionLevelLabel = (
  currentProject: any,
  value: number
): { label: string; translate: boolean } => {
  const option = getSelectionLevelOptions(currentProject).find((o) => o.value === value);
  if (option) return { label: option.label, translate: option.translateLabel };
  // Unknown level (e.g. cross-project data): translate the default enum key when known.
  const enumKey = intToSelectionLevelEnum[value];
  return enumKey ? { label: enumKey, translate: true } : { label: String(value), translate: false };
};

/**
 * Cross-project label resolver: resolve a level's display label from a plain levels array (a
 * single project's `selectionLevels` from the All Applications payload) instead of an Immutable
 * project. When `levels` is missing (feature flag off / project not customised) it falls back to
 * the default enum key, so the caller renders exactly the legacy translated label.
 */
export const getSelectionLevelLabelFromLevels = (
  levels: DynamicSelectionLevel[] | null | undefined,
  value: number
): { label: string; translate: boolean } => {
  const option =
    levels && levels.length
      ? buildSelectionLevelOptionsFromLevels(levels).find((o) => o.value === value)
      : undefined;
  if (option) return { label: option.label, translate: option.translateLabel };
  const enumKey = intToSelectionLevelEnum[value];
  return enumKey ? { label: enumKey, translate: true } : { label: String(value), translate: false };
};

/**
 * Index the GraphQL `projectSelectionLevels` list into a `{ [projectId]: levels[] }` lookup so a
 * cross-project list can resolve each row's label by its project id. Pages accumulate this map
 * across pagination, so later batches merge in their projects without dropping earlier ones.
 */
export const indexSelectionLevelsByProject = (
  projectSelectionLevels:
    | Array<{ projectId: number; selectionLevels: DynamicSelectionLevel[] }>
    | null
    | undefined
): Record<number, DynamicSelectionLevel[]> => {
  const map: Record<number, DynamicSelectionLevel[]> = {};
  (projectSelectionLevels || []).forEach((entry) => {
    if (entry && entry.projectId != null) {
      map[entry.projectId] = entry.selectionLevels || [];
    }
  });
  return map;
};

/**
 * The next visible level (by order) after `currentValue`, or null when already at the last visible
 * level (Hired) or no further visible level exists. Order-based so it respects reordering and skips
 * hidden levels — the dynamic replacement for the legacy hardcoded findNextAvailableSelectionLevel.
 */
export const getNextSelectionLevelValue = (
  currentProject: any,
  currentValue: number
): number | null => {
  const all = getSelectionLevelOptions(currentProject);
  const current = all.find((o) => o.value === currentValue);
  const currentOrder = current ? current.order : -1;
  const next = all
    .filter((o) => !o.hidden && o.order > currentOrder)
    .sort((a, b) => a.order - b.order)[0];
  return next ? next.value : null;
};

/**
 * Whether the Hired column has been renamed. Prefers the authoritative backend flag
 * `hiredColumnRenamed` when it is a boolean; otherwise falls back to a locale-aware comparison of
 * the current label against the translated default (an empty/blank label is never a real rename).
 * Relocated from the legacy inline editor so the selection-levels drawer wiring can share it.
 */
export const computeHiredRenamed = (
  hiredColumnRenamed: boolean | undefined,
  currentLabel: string | undefined,
  defaultLabel: string
): boolean => {
  if (typeof hiredColumnRenamed === 'boolean') return hiredColumnRenamed;
  const trimmed = (currentLabel || '').trim();
  return !!trimmed && trimmed !== defaultLabel;
};
